import { createFileRoute } from "@tanstack/react-router";
import { useContent } from "@/lib/use-content";
import { Brain, Focus, Heart, AlertCircle, XCircle } from "lucide-react";

export const Route = createFileRoute("/support")({
  head: () => ({
    meta: [
      { title: "טיפולים ומענים רגשיים | ישיבת תורת חיים" },
      { name: "description", content: "מענה מותאם לתלמידים עם לקויות למידה, קשיי קשב וקושי רגשי." },
    ],
  }),
  component: Support,
});

const ICONS = [Brain, Focus, Heart];

function Support() {
  const c = useContent("support");
  return (
    <>
      <section className="bg-primary text-primary-foreground py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="mx-auto mb-4 h-px w-16 bg-gold" />
          <h1 className="font-display text-5xl font-bold">{c.title}</h1>
          <p className="mt-3 text-xl text-gold font-display">{c.subtitle}</p>
        </div>
      </section>

      {c.coverImage && (
        <section className="container mx-auto px-4 pt-10">
          <img src={c.coverImage} alt={c.title} className="rounded-lg shadow-elegant w-full h-auto max-w-5xl mx-auto" loading="lazy" />
        </section>
      )}

      <section className="container mx-auto px-4 py-16 max-w-4xl text-center">
        <p className="font-display text-2xl text-primary leading-relaxed">{c.intro}</p>
      </section>

      <section className="container mx-auto px-4 pb-16">
        <div className="grid gap-6 md:grid-cols-3 max-w-6xl mx-auto">
          {c.areas?.map((area: any, i: number) => {
            const Icon = ICONS[i] ?? Brain;
            return (
              <div key={i} className="rounded-lg bg-card shadow-card border border-border/50 hover:shadow-elegant transition-shadow overflow-hidden">
                {area.image && (
                  <img src={area.image} alt={area.title} className="w-full h-44 object-cover" loading="lazy" />
                )}
                <div className="p-7">
                  <div className="flex h-12 w-12 items-center justify-center rounded-md gradient-gold text-gold-foreground shadow-card">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display text-2xl font-bold text-primary">{area.title}</h3>
                  <p className="mt-3 text-foreground/75 leading-relaxed whitespace-pre-line">{area.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="rounded-lg bg-card p-8 shadow-card border border-gold/30">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-gold" />
              <h2 className="font-display text-2xl font-bold text-primary">{c.signsTitle}</h2>
            </div>
            <ul className="space-y-2 mb-4">
              {c.signs?.map((s: string, i: number) => (
                <li key={i} className="flex items-start gap-3 text-foreground/85">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed border-t border-border pt-4">
              {c.signsFooter}
            </p>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="text-center mb-8">
          <div className="mx-auto mb-3 h-px w-12 bg-gold" />
          <h2 className="font-display text-3xl font-bold text-primary">{c.notFitTitle}</h2>
        </div>
        <ul className="grid gap-3 md:grid-cols-2">
          {c.notFit?.map((item: string, i: number) => (
            <li key={i} className="flex items-start gap-3 rounded-md bg-card border border-border p-4">
              <XCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <span className="text-foreground/80 text-sm leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
